import React, { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";

import useMutation from "../../../../hooks/useMutation";
import { CREATE_GROUPED_ASSET_MEMBER_MUTATION } from "../../../../constants/graphql/mutations";
import { useGroupedAsset } from "../../../../contexts/grouped-asset";
import AssetDetailsModal from "../../../../views/inventory/requests/asset-details/AssetDetailsModal";
import AddReferenceAssetModal from "../../../project/grouped_items/AddReferenceAssetModal";
import ConfirmDeleteModal from "../../../images/ConfirmDeleteModal";

export default function GroupedAssetBuilder({ disabled, entity, projectId, refetch }) {
  const { groupedAsset, removeGroupedAssetMember, refetch: refetchGroupedAsset } = useGroupedAsset();
  const [showAddModal, setShowAddModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [createMember, { loading: creating }] = useMutation(CREATE_GROUPED_ASSET_MEMBER_MUTATION);

  const currentEntity = groupedAsset?.id ? groupedAsset : entity;
  const referenceMember = currentEntity?.groupedAssetMembers?.[0];
  const hasReferenceMember = !!referenceMember;
  const memberAssetsData = currentEntity?.groupedMemberAssets;
  const loading = creating;

  const handleAddMember = async (assetId) => {
    await createMember({
      variables: {
        groupedAssetId: currentEntity.id,
        input: { assetId, quantity: 1 }
      },
      onSuccess: () => {
        setShowAddModal(false);
        refetchGroupedAsset && refetchGroupedAsset();
        refetch && refetch();
      },
      onFailure: () => {
        setShowAddModal(false);
      }
    });
  };

  const handleRemoveMember = async () => {
    if (!itemToRemove) return;

    await removeGroupedAssetMember(itemToRemove.id);
    setItemToRemove(null);
    refetchGroupedAsset && refetchGroupedAsset();
    refetch && refetch();
  };

  return (
    <Paper>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography fontWeight={500} fontSize={18}>
            Reference Item
          </Typography>
          {!hasReferenceMember && !disabled && (
            <Button
              variant="outlined"
              onClick={() => setShowAddModal(true)}
              disabled={loading}
            >
              Add Reference Item
            </Button>
          )}
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        )}

        {hasReferenceMember && memberAssetsData ? (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">SKU Number</TableCell>
                  <TableCell align="center">Associated Item IDs</TableCell>
                  <TableCell align="center">Description</TableCell>
                  <TableCell align="center">Total Available Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell align="center">{memberAssetsData.sku_number}</TableCell>
                  <TableCell align="center">
                    {memberAssetsData.associated_item_ids && memberAssetsData.associated_item_ids.length > 0
                      ? memberAssetsData.associated_item_ids.map((itemId, idx) => (
                          <div key={idx}>
                            <AssetDetailsModal
                              asset={{
                                id: memberAssetsData.associated_asset_ids[idx],
                                itemId: itemId,
                                project: { id: projectId },
                              }}
                            />
                          </div>
                        ))
                      : "—"}
                  </TableCell>
                  <TableCell align="center">{memberAssetsData.description}</TableCell>
                  <TableCell align="center">{memberAssetsData.total_available_quantity}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {!disabled && (
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setItemToRemove(referenceMember)}
                  disabled={loading}
                >
                  Remove Reference Item
                </Button>
              </Box>
            )}
          </>
        ) : (
          !loading && (
            <Box py={4} display="flex" justifyContent="center">
              <Typography color="textSecondary">
                No reference item added yet.
              </Typography>
            </Box>
          )
        )}
      </Box>

      {showAddModal && (
        <AddReferenceAssetModal
          projectId={projectId}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddMember}
        />
      )}

      {itemToRemove && (
        <ConfirmDeleteModal
          title="Remove Reference Item"
          descr="Are you sure you want to remove this reference item? This will prevent quantity aggregation."
          onClose={() => setItemToRemove(null)}
          onDelete={handleRemoveMember}
          applyLabel="Remove"
        />
      )}
    </Paper>
  );
}

